import { useCallback, useEffect, useRef, useState } from "react";
import axios from "axios";
import {
  X, Camera, Upload, ArrowsClockwise, CircleNotch, CheckCircle, Warning, Question, IdentificationCard, BookOpen,
} from "@phosphor-icons/react";
import { MRZ_T } from "@/lib/mrzTranslations";

const API_BASE = process.env.REACT_APP_BACKEND_URL + "/api";

const RATE_MAX = 3;
const RATE_WINDOW = 60 * 1000;

export default function MRZScanner({ isOpen, onClose, onScanComplete, lang = "hr" }) {
  const t = MRZ_T[lang] || MRZ_T.hr;
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const fileRef = useRef(null);
  const streamRef = useRef(null);
  const [mode, setMode] = useState("intro"); // intro|camera|preview|scanning|success|error|ratelimit
  const [captured, setCaptured] = useState(null);
  const [errorMsg, setErrorMsg] = useState("");
  const [attempts, setAttempts] = useState([]);
  const [countdown, setCountdown] = useState(0);

  // Cleanup on unmount
  useEffect(() => {
    return () => stopStream();
  }, []);

  // Reset modes when opened
  useEffect(() => {
    if (isOpen) {
      setMode("intro");
      setCaptured(null);
      setErrorMsg("");
    } else {
      stopStream();
    }
  }, [isOpen]);

  // Start camera when entering camera mode
  useEffect(() => {
    if (!isOpen || mode !== "camera") return;
    let cancelled = false;
    (async () => {
      try {
        let stream;
        try {
          stream = await navigator.mediaDevices.getUserMedia({ video: { facingMode: { exact: "environment" }, width: { ideal: 1920 }, height: { ideal: 1080 } } });
        } catch {
          stream = await navigator.mediaDevices.getUserMedia({ video: { width: { ideal: 1920 }, height: { ideal: 1080 } } });
        }
        if (cancelled) { stream.getTracks().forEach((tr) => tr.stop()); return; }
        streamRef.current = stream;
        if (videoRef.current) {
          videoRef.current.srcObject = stream;
          await videoRef.current.play();
        }
      } catch (err) {
        setErrorMsg(t.cameraError);
        setMode("error");
      }
    })();
    return () => { cancelled = true; stopStream(); };
  }, [isOpen, mode, t]);

  // Countdown timer for rate limit
  useEffect(() => {
    if (countdown <= 0) return;
    const id = setTimeout(() => setCountdown((c) => c - 1), 1000);
    return () => clearTimeout(id);
  }, [countdown]);

  const stopStream = () => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((tr) => tr.stop());
      streamRef.current = null;
    }
  };

  const compressFull = (srcCanvas) => {
    // Aggressive client-side downscale: keeps MRZ characters readable while
    // dramatically cutting upload + Gemini processing time.
    const maxW = 900;
    const c = document.createElement("canvas");
    let w = srcCanvas.width, h = srcCanvas.height;
    if (w > maxW) { h = Math.round((h * maxW) / w); w = maxW; }
    c.width = w; c.height = h;
    const ctx = c.getContext("2d");
    ctx.imageSmoothingQuality = "high";
    ctx.drawImage(srcCanvas, 0, 0, w, h);
    return c.toDataURL("image/jpeg", 0.72);
  };

  const takePhoto = useCallback(() => {
    if (!videoRef.current || !canvasRef.current) return;
    const v = videoRef.current;
    const c = canvasRef.current;
    c.width = v.videoWidth; c.height = v.videoHeight;
    c.getContext("2d").drawImage(v, 0, 0);
    setCaptured(compressFull(c));
    setMode("preview");
    stopStream();
  }, []);

  const onFile = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (ev) => {
      const img = new Image();
      img.onload = () => {
        const c = document.createElement("canvas");
        c.width = img.width; c.height = img.height;
        c.getContext("2d").drawImage(img, 0, 0);
        setCaptured(compressFull(c));
        setMode("preview");
      };
      img.src = ev.target.result;
    };
    reader.readAsDataURL(file);
    e.target.value = "";
  };

  const checkRate = () => {
    const now = Date.now();
    const recent = attempts.filter((a) => now - a < RATE_WINDOW);
    setAttempts(recent);
    if (recent.length >= RATE_MAX) {
      const oldest = Math.min(...recent);
      const wait = Math.ceil((RATE_WINDOW - (now - oldest)) / 1000);
      setCountdown(wait);
      setMode("ratelimit");
      return false;
    }
    return true;
  };

  const analyze = async () => {
    if (!captured) return;
    if (!checkRate()) return;
    setAttempts((a) => [...a, Date.now()]);
    setMode("scanning");
    setErrorMsg("");
    try {
      const { data } = await axios.post(`${API_BASE}/mrz/scan`, { image_base64: captured });
      if (data.success && data.data) {
        setMode("success");
        setTimeout(() => { onScanComplete(data.data); onClose(); }, 800);
      } else {
        setErrorMsg(data.error || t.error);
        setMode("error");
      }
    } catch (e) {
      setErrorMsg(e?.response?.data?.detail || e.message || "Network error");
      setMode("error");
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[100] flex flex-col bg-black/95 text-white" data-testid="mrz-scanner">
      {/* Header */}
      <div className="flex items-center justify-between border-b border-white/10 px-4 py-3">
        <button onClick={onClose} className="rounded-full p-1.5 hover:bg-white/10" data-testid="mrz-close"><X size={20} /></button>
        <h2 className="text-sm font-semibold">{t.title}</h2>
        <button onClick={() => setMode("intro")} className="rounded-full p-1.5 hover:bg-white/10" data-testid="mrz-help"><Question size={20} /></button>
      </div>

      {/* Modes */}
      {mode === "intro" && (
        <div className="flex-1 overflow-y-auto bg-white px-5 py-8 text-slate-800" data-testid="mrz-intro">
          <h3 className="text-center text-2xl font-bold">{t.introTitle}</h3>
          <p className="mx-auto mt-3 max-w-md text-center text-sm text-slate-500">{t.introDesc}</p>
          <div className="mx-auto mt-5 max-w-md rounded-xl bg-slate-100 p-3">
            <p className="text-center text-sm font-semibold">{t.howLooks}</p>
            <div className="mt-2 rounded-lg border-2 border-dashed border-amber-700 bg-white p-3 text-center font-mono text-xs">
              P&lt;HRV&lt;&lt;IME&lt;&lt;PREZIME&lt;&lt;&lt;&lt;&lt;<br />
              12345678&lt;&lt;&lt;&lt;HRV&lt;&lt;&lt;&lt;&lt;&lt;&lt;&lt;
            </div>
          </div>
          <div className="mx-auto mt-4 grid max-w-md grid-cols-2 gap-3">
            <div className="rounded-xl border border-slate-200 bg-white p-3">
              <span className="inline-block rounded bg-blue-500 px-2 py-1 text-[10px] font-bold text-white">ID</span>
              <p className="mt-1 text-xs">{t.idBack}</p>
            </div>
            <div className="rounded-xl border border-slate-200 bg-white p-3">
              <span className="inline-block rounded bg-rose-500 px-2 py-1 text-[10px] font-bold text-white">PAS</span>
              <p className="mt-1 text-xs">{t.passportPage}</p>
            </div>
          </div>
          <div className="mx-auto mt-4 max-w-md rounded-lg bg-amber-50 px-3 py-2 text-xs text-amber-900">
            💡 {t.introTip}
          </div>
          <button onClick={() => setMode("camera")} className="mx-auto mt-6 block w-full max-w-md rounded-xl bg-[#FF990A] px-6 py-3 text-sm font-bold text-white shadow-md hover:bg-[#E68600]" data-testid="mrz-start">
            {t.startScan}
          </button>
        </div>
      )}

      {mode === "camera" && (
        <>
          <div className="relative flex flex-1 items-center justify-center overflow-hidden">
            <video ref={videoRef} className="absolute inset-0 h-full w-full object-cover" playsInline muted />
            <div className="pointer-events-none absolute inset-0 flex flex-col">
              <div className="flex-1 bg-black/55" />
              <div className="relative h-44">
                <div className="absolute inset-x-6 inset-y-0 rounded-xl border-2 border-white/95">
                  <span className="absolute -top-0.5 -left-0.5 h-7 w-7 rounded-tl-xl border-l-4 border-t-4 border-emerald-400" />
                  <span className="absolute -top-0.5 -right-0.5 h-7 w-7 rounded-tr-xl border-r-4 border-t-4 border-emerald-400" />
                  <span className="absolute -bottom-0.5 -left-0.5 h-7 w-7 rounded-bl-xl border-l-4 border-b-4 border-emerald-400" />
                  <span className="absolute -bottom-0.5 -right-0.5 h-7 w-7 rounded-br-xl border-r-4 border-b-4 border-emerald-400" />
                  <div className="flex h-full flex-col items-center justify-center font-mono text-[10px] text-emerald-300">
                    <span>P&lt;HRV&lt;&lt;IME&lt;&lt;PREZIME&lt;&lt;&lt;&lt;&lt;&lt;&lt;&lt;&lt;</span>
                    <span>123456789&lt;&lt;&lt;&lt;HRV&lt;&lt;&lt;&lt;&lt;&lt;&lt;&lt;&lt;</span>
                  </div>
                </div>
              </div>
              <p className="bg-black/55 text-center text-sm text-white/90 pt-2">{t.guideHint}</p>
              <div className="flex-1 bg-black/55" />
            </div>
          </div>
          <div className="space-y-2 border-t border-white/10 bg-black/80 p-4">
            <button onClick={takePhoto} className="flex w-full items-center justify-center gap-2 rounded-xl bg-[#FF990A] px-4 py-3 text-base font-bold text-white shadow-lg" data-testid="mrz-shoot">
              <Camera size={18} /> {t.takePhoto}
            </button>
            <button onClick={() => fileRef.current?.click()} className="flex w-full items-center justify-center gap-2 rounded-xl border border-white/30 px-4 py-2.5 text-sm font-semibold" data-testid="mrz-upload">
              <Upload size={16} /> {t.uploadPhoto}
            </button>
            <input ref={fileRef} type="file" accept="image/*" onChange={onFile} className="hidden" />
            <button onClick={onClose} className="w-full px-4 py-1.5 text-xs text-white/70">{t.close}</button>
          </div>
        </>
      )}

      {mode === "preview" && captured && (
        <>
          <div className="flex flex-1 items-center justify-center overflow-y-auto p-4">
            <img src={captured} alt="preview" className="max-h-full max-w-full rounded-lg" />
          </div>
          <div className="space-y-2 border-t border-white/10 bg-black/80 p-4">
            <button onClick={analyze} className="flex w-full items-center justify-center gap-2 rounded-xl bg-[#FF990A] px-4 py-3 text-base font-bold text-white shadow-lg" data-testid="mrz-analyze">
              <BookOpen size={18} /> {t.analyze}
            </button>
            <button onClick={() => { setCaptured(null); setMode("camera"); }} className="flex w-full items-center justify-center gap-2 rounded-xl border border-white/30 px-4 py-2.5 text-sm font-semibold" data-testid="mrz-retake">
              <ArrowsClockwise size={16} /> {t.newPhoto}
            </button>
          </div>
        </>
      )}

      {mode === "scanning" && (
        <div className="flex flex-1 flex-col items-center justify-center gap-3 px-6 text-center" data-testid="mrz-scanning">
          <CircleNotch size={48} className="animate-spin text-[#FF990A]" />
          <p className="text-base font-semibold">{t.scanning}</p>
          <p className="text-xs text-white/60">{t.scanningDetail}</p>
          <p className="mt-3 max-w-sm rounded-lg bg-white/10 px-3 py-2 text-[11px] leading-relaxed text-white/80" data-testid="mrz-scanning-wait">
            {t.scanningWait}
          </p>
        </div>
      )}

      {mode === "success" && (
        <div className="flex flex-1 flex-col items-center justify-center gap-3" data-testid="mrz-success">
          <CheckCircle size={56} weight="fill" className="text-emerald-400" />
          <p className="text-lg font-bold">{t.success}</p>
        </div>
      )}

      {mode === "error" && (
        <div className="flex flex-1 flex-col items-center justify-center gap-3 px-6 text-center" data-testid="mrz-error">
          <Warning size={56} weight="fill" className="text-rose-400" />
          <p className="text-lg font-bold">{errorMsg || t.error}</p>
          <p className="max-w-sm text-xs text-white/60">{t.errorTip}</p>
          <div className="mt-4 flex w-full max-w-sm flex-col gap-2">
            <button onClick={() => { setCaptured(null); setErrorMsg(""); setMode("camera"); }} className="rounded-xl bg-[#FF990A] px-4 py-2.5 text-sm font-bold text-white">{t.retry}</button>
            <button onClick={onClose} className="px-4 py-1.5 text-xs text-white/70">{t.close}</button>
          </div>
        </div>
      )}

      {mode === "ratelimit" && (
        <div className="flex flex-1 flex-col items-center justify-center gap-3 px-6 text-center" data-testid="mrz-ratelimit">
          <Warning size={56} weight="fill" className="text-amber-400" />
          <p className="text-lg font-bold">{t.rateTitle}</p>
          <p className="text-xs text-white/60">{t.rateDesc}</p>
          <p className="mt-4 text-sm text-white/70">{t.rateWait}</p>
          <p className="text-5xl font-bold text-[#FF990A]">{countdown}</p>
          <p className="text-xs text-white/50">{t.seconds}</p>
          <button onClick={onClose} className="mt-6 px-4 py-1.5 text-xs text-white/70">{t.close}</button>
        </div>
      )}

      <canvas ref={canvasRef} className="hidden" />
    </div>
  );
}

// CTA card that opens the scanner
export function MRZScanButton({ onOpen, lang = "hr", style }) {
  const t = MRZ_T[lang] || MRZ_T.hr;
  return (
    <>
      <button
        type="button"
        onClick={onOpen}
        className="flex w-full items-center gap-4 rounded-[var(--cf-radius)] border-2 p-4 text-left transition hover:shadow-md"
        style={{ background: `color-mix(in oklab, ${style?.primary_color || "#FF990A"} 12%, white)`, borderColor: `color-mix(in oklab, ${style?.primary_color || "#FF990A"} 30%, white)` }}
        data-testid="mrz-cta"
      >
        <span className="inline-flex h-12 w-12 shrink-0 items-center justify-center rounded-full" style={{ background: style?.primary_color || "#FF990A" }}>
          <Camera size={22} weight="bold" className="text-white" />
        </span>
        <span className="flex-1">
          <span className="block text-base font-bold" style={{ color: "var(--cf-text)" }}>{t.scanCta}</span>
          <span className="block text-xs" style={{ color: "var(--cf-text-muted)" }}>{t.scanCtaSub}</span>
        </span>
        <IdentificationCard size={20} style={{ color: style?.primary_color || "#FF990A" }} />
      </button>
      <div className="my-5 flex items-center gap-3">
        <div className="h-px flex-1" style={{ background: "var(--cf-border)" }} />
        <span className="text-xs" style={{ color: "var(--cf-text-muted)" }}>{t.orManual}</span>
        <div className="h-px flex-1" style={{ background: "var(--cf-border)" }} />
      </div>
    </>
  );
}
